import React, { useEffect, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { PolicyModal } from '../components/PolicyModal';
import { TeamModal } from '../components/TeamModal';
import { isTeamInUse, KeystoneUser, Team } from '../components/teams';

/** Lists registered teams (the user_team directory) and lets the user register, rename and delete them. */
export const TeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamUsers, setTeamUsers] = useState<KeystoneUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [modal, setModal] = useState<{ team?: Team } | null>(null);
  const [toDelete, setToDelete] = useState<Team | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { addNotification } = useNotifications();

  const load = () => {
    setLoading(true);
    Promise.all([api('GET', '/team_users'), api('GET', '/users?user_type=TEAM')])
      .then(([t, u]) => {
        setTeams(Array.isArray(t) ? t : []);
        setTeamUsers(Array.isArray(u) ? u : []);
      })
      .catch(e => addNotification('error', `Failed to load teams: ${(e as Error).message}`))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api('DELETE', '/team_user', toDelete);
      addNotification('success', `Deleted team '${toDelete.team_name}'`);
      setToDelete(null);
      load();
    } catch (e) {
      setDeleteError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const copyId = (team: Team) =>
    navigator.clipboard
      .writeText(team.team_id)
      .then(() => addNotification('success', 'Team ID copied'))
      .catch(() => addNotification('error', 'Could not copy the team ID'));

  const q = filterText.toLowerCase();
  const filtered = teams.filter(t => !q || t.team_name.toLowerCase().includes(q) || t.team_id.toLowerCase().includes(q));

  return (
    <SpaceBetween size="l">
      <Table
        loading={loading}
        loadingText="Loading teams"
        header={
          <Header
            variant="h1"
            counter={`(${filtered.length})`}
            description="Teams must be registered here before they can be added as TEAM users."
            actions={
              <Button variant="primary" onClick={() => setModal({})}>
                Register team
              </Button>
            }
          >
            Teams
          </Header>
        }
        filter={
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder="Filter by name or ID"
          />
        }
        columnDefinitions={[
          { id: 'name', header: 'Team name', cell: t => t.team_name, sortingField: 'team_name' },
          { id: 'id', header: 'Team ID', cell: t => t.team_id, sortingField: 'team_id' },
          { id: 'used', header: 'Used', cell: t => (isTeamInUse(t, teamUsers) ? 'Yes' : 'No'), width: 100 },
          {
            id: 'actions',
            header: '',
            width: 80,
            cell: t => {
              const inUse = isTeamInUse(t, teamUsers);
              return (
                <ButtonDropdown
                  variant="icon"
                  ariaLabel={`Actions for ${t.team_name}`}
                  expandToViewport
                  items={[
                    { id: 'rename', text: 'Rename' },
                    {
                      id: 'delete',
                      text: 'Delete',
                      disabled: inUse,
                      disabledReason: inUse ? 'A TEAM user refers to this team. Delete that user first.' : undefined,
                    },
                    { id: 'copy', text: 'Copy ID' },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'rename') setModal({ team: t });
                    if (detail.id === 'delete') {
                      setDeleteError(null);
                      setToDelete(t);
                    }
                    if (detail.id === 'copy') copyId(t);
                  }}
                />
              );
            },
          },
        ]}
        items={filtered}
        empty={<Box textAlign="center" padding="l">No teams registered</Box>}
      />

      <TeamModal
        visible={!!modal}
        team={modal?.team}
        teams={teams}
        onCancel={() => setModal(null)}
        onSaved={() => {
          setModal(null);
          load();
        }}
      />

      <PolicyModal
        visible={!!toDelete}
        header="Delete team"
        confirmText="Delete"
        busy={deleting}
        error={deleteError}
        errorHeader="Could not delete team"
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      >
        <Box>
          Delete team <b>{toDelete?.team_name}</b> (<code>{toDelete?.team_id}</code>)? It can no longer be added as a
          TEAM user until it is registered again.
        </Box>
      </PolicyModal>
    </SpaceBetween>
  );
};
